import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useOutletContext, useSearchParams, Link } from 'react-router-dom';
import PageHeader from '../components/ui/PageHeader';
import RoiCountryTreeTable from '../components/roi/RoiCountryTreeTable';
import RoiDateTable from '../components/roi/RoiDateTable';
import TimezoneSwitcher from '../components/ui/TimezoneSwitcher';
import useReportTimezone from '../hooks/useReportTimezone';
import RoiSummaryBoards from '../components/roi/RoiSummaryBoards';
import RoiInventoryEarnOverview from '../components/roi/RoiInventoryEarnOverview';
import CompareRangeBar from '../components/ui/CompareRangeBar';
import SavePresetButton from '../components/ui/SavePresetButton';
import ThresholdAlertBanner from '../components/ui/ThresholdAlertBanner';
import { adsAPI, roiAPI, usersAPI } from '../utils/api';
import MultiSelect from '../components/ui/MultiSelect';
import { normalizeHostInput, isValidHost } from '../utils/filters/hostInput';
import RoiCurrencySelect from '../components/roi/RoiCurrencySelect';
import { useRoiDisplayCurrency } from '../utils/report/roiCurrency';
import { addRoiSheet, sheetAccountId, useRoiSheets } from '../utils/report/roiSheets';
import RoiFilterRow from '../components/roi/RoiFilterRow';
import RoiFilterSection from '../components/roi/RoiFilterSection';
import useFirebaseSiteDetect from '../hooks/useFirebaseSiteDetect';
import {
  ALL_SENTINEL,
  isAllSelection,
  toAllSelection,
  collapseFullSelection,
  optionValues,
} from '../utils/inventorySelection';
import { DATE_PRESETS } from '../utils/gamReportCatalog';
import {
  getDateRestriction,
  clampPresetRange,
  clampDateRange,
  defaultReportRangeForUser,
  allowedDatePresets,
  isCustomRangeIncomplete,
  isFixedDateRestriction,
  formatDateRestrictionLabel,
  clampDateValue,
} from '../utils/dateRestriction';
import { useAuth } from '../store/useAuth';
import { saveReportPage } from '../store/slices/reportSlice';
import { isReportCacheFresh, slimRoiForCache } from '../hooks/useReportPageCache';
import { nowTimeInTZ } from '../utils/datetime';
import { getUserFacingMessage, logErrorForDebug } from '../utils/userFacingError';
import { useMedia } from '../hooks/useMedia';
import { showToast } from '../hooks/useToast';
import { parseReportShare, copyReportLink } from '../utils/reportShare';
import { PRESET_PAGES, filtersOnlySnapshot } from '../utils/reportPresets';
import { getLastPageFilters, saveLastPageFilters, LAST_FILTER_PAGES } from '../utils/lastPageFilters';
import { loadComparePrefs, saveComparePrefs } from '../utils/dashCharts';
import {
  resolveCompareRange,
  compareLabelFor,
  pctChange,
  previousPeriodRange,
  isPeriodAllowed,
} from '../utils/periodCompare';
import { evaluateRoiThresholds } from '../utils/thresholdAlerts';
import {
  buildCountryTree,
  formatRoiMoney,
  formatRoiPct,
  labelsForSelection,
  mergeRoiBreakdownPayload,
  mergeRoiSummaryPayload,
  roiToneClass,
  sumInventoryEarnFromBreakdown,
} from '../utils/report/roiView';

const ROI_TIP_KEY = 'adnexus.guide.roi.v1';
const ROI_POLL_MS = 30 * 60 * 1000;

// "All selected" App IDs still auto-link Firebase subdomains while the list has at most this many apps.
// 200 = what POST /ads/firebase/resolve-sites accepts per request (it resolves per Firebase project, cached), so an
// account with a normal number of apps gets its subdomains filled in without picking each app by hand.
const FIREBASE_AUTO_ALL_MAX_APPS = 200;

function money(n, currency = 'USD') {
  return formatRoiMoney(n, currency);
}

function pct(n) {
  return formatRoiPct(n);
}

function roiTone(n) {
  return roiToneClass(n);
}

function emptyExpenseSlot() {
  return {
    amount: '',
    label: '',
    targetType: 'general',
    targetKey: '',
    notes: '',
  };
}

function defaultRoiApplied(user, todayInit) {
  const r = clampPresetRange('today', getDateRestriction(user));
  return {
    startDate: r?.startDate || todayInit.startDate,
    endDate: r?.endDate || todayInit.endDate,
    targetType: 'all',
    accountIds: null,
    campaignIds: null,
    appKeys: null,
    siteKeys: null,
    countryCodes: null,
  };
}

function roiLoadKeyFromApplied(applied, userId) {
  return JSON.stringify({
    startDate: applied?.startDate || null,
    endDate: applied?.endDate || null,
    targetType: applied?.targetType || 'all',
    accountIds: applied?.accountIds || null,
    campaignIds: applied?.campaignIds || null,
    appKeys: applied?.appKeys || null,
    siteKeys: applied?.siteKeys || null,
    countryCodes: applied?.countryCodes || null,
    userId: userId || null,
  });
}

function RoiView() {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const savedRaw = useSelector((s) => s.reports?.roi);
  const saved = (
    (!savedRaw?.userId || savedRaw.userId === user?.id)
    && user?.clientId
    && savedRaw?.clientId
    && String(savedRaw.clientId) === String(user.clientId)
  ) ? savedRaw : null;
  const cacheFresh = isReportCacheFresh(saved, ROI_POLL_MS, { clientId: user?.clientId });
  const outlet = useOutletContext() || {};
  const mergedNetworkIds = useMemo(
    () => (user?.role === 'admin' ? [] : (Array.isArray(outlet.mergeNetworkIds) ? outlet.mergeNetworkIds : []).filter(Boolean)),
    [user?.role, (outlet.mergeNetworkIds || []).join(',')]
  );
  const networkInfo = outlet.networkInfo;
  const [searchParams] = useSearchParams();
  const dateRestriction = useMemo(() => getDateRestriction(user), [user]);
  const dateFilterLocked = isFixedDateRestriction(dateRestriction);
  const presetOptions = useMemo(
    () => (dateFilterLocked ? [] : allowedDatePresets(dateRestriction, DATE_PRESETS)),
    [dateRestriction, dateFilterLocked]
  );
  const todayInit = useMemo(() => defaultReportRangeForUser(user), [user]);

  const [preset, setPreset] = useState(() => saved?.preset ?? 'today');
  const [startDate, setStartDate] = useState(() => (
    saved?.startDate || saved?.applied?.startDate
    || clampPresetRange('today', getDateRestriction(user))?.startDate
    || todayInit.startDate
  ));
  const [endDate, setEndDate] = useState(() => (
    saved?.endDate || saved?.applied?.endDate
    || clampPresetRange('today', getDateRestriction(user))?.endDate
    || todayInit.endDate
  ));
  const [applied, setApplied] = useState(() => (
    saved?.applied && saved.applied.startDate
      ? saved.applied
      : defaultRoiApplied(user, todayInit)
  ));
  const [filterAccountIds, setFilterAccountIds] = useState(() => (
    saved?.filterAccountIds?.length ? saved.filterAccountIds : toAllSelection()
  ));
  const [filterCampaignIds, setFilterCampaignIds] = useState(() => (
    saved?.filterCampaignIds?.length ? saved.filterCampaignIds : toAllSelection()
  ));
  const [filterAppKeys, setFilterAppKeys] = useState(() => (
    saved?.filterAppKeys?.length ? saved.filterAppKeys : toAllSelection()
  ));
  const [filterSiteKeys, setFilterSiteKeys] = useState(() => saved?.filterSiteKeys || []);
  const [filterCountryCodes, setFilterCountryCodes] = useState(() => (
    saved?.filterCountryCodes?.length ? saved.filterCountryCodes : toAllSelection()
  ));
  const [roiAccountOptions, setRoiAccountOptions] = useState(() => saved?.roiAccountOptions || []);
  const [roiCampaignOptions, setRoiCampaignOptions] = useState(() => saved?.roiCampaignOptions || []);
  const [roiAppOptions, setRoiAppOptions] = useState(() => saved?.roiAppOptions || []);
  const [roiCountryOptions, setRoiCountryOptions] = useState(() => saved?.roiCountryOptions || []);
  const [roiSiteOptions, setRoiSiteOptions] = useState(() => saved?.roiSiteOptions || []);
  const [roiSitesLoading, setRoiSitesLoading] = useState(false);
  const [roiCampaignsLoading, setRoiCampaignsLoading] = useState(false);
  const [roiCampaignsFallback, setRoiCampaignsFallback] = useState(false);
  const [roiAppsLoading, setRoiAppsLoading] = useState(false);
  const [roiCountriesLoading, setRoiCountriesLoading] = useState(false);
  const [roiCountriesFallback, setRoiCountriesFallback] = useState(false);
  const [earnTz, setEarnTz] = useState(null);
  const [data, setData] = useState(() => (cacheFresh || saved?.data ? saved?.data : null) ?? null);
  const [loading, setLoading] = useState(() => !saved?.data);
  const [breakdownLoading, setBreakdownLoading] = useState(() => (
    !saved?.data?.countryTargetBreakdown?.length && !saved?.data?.countryBreakdown?.length
  ));
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(() => saved?.lastUpdated ?? null);
  const [fetchedAt, setFetchedAt] = useState(() => saved?.fetchedAt ?? null);
  const [filtersOpen, setFiltersOpen] = useState(true);

  const [compareMode, setCompareMode] = useState(() => loadComparePrefs(user?.id).mode);
  const [compareStart, setCompareStart] = useState(() => loadComparePrefs(user?.id).startDate);
  const [compareEnd, setCompareEnd] = useState(() => loadComparePrefs(user?.id).endDate);
  const [priorSummary, setPriorSummary] = useState(null);
  const [thresholdBanners, setThresholdBanners] = useState([]);
  const [adsSyncHealth, setAdsSyncHealth] = useState(null);
  const [showRoiTip, setShowRoiTip] = useState(() => {
    try {
      return localStorage.getItem(ROI_TIP_KEY) !== 'done';
    } catch {
      return true;
    }
  });

  const [showExpense, setShowExpense] = useState(false);
  const [expenseSharedDate, setExpenseSharedDate] = useState(() => endDate);
  const [expense1, setExpense1] = useState(emptyExpenseSlot);
  const [expense2, setExpense2] = useState(emptyExpenseSlot);
  const [expenseBusy, setExpenseBusy] = useState(false);
  const [siteHosts, setSiteHosts] = useState([]);
  const [appIds, setAppIds] = useState([]);
  const [inventoryLoading, setInventoryLoading] = useState(false);

  const [countryPage, setCountryPage] = useState(1);
  const [countrySearch, setCountrySearch] = useState('');
  const [tableDensity, setTableDensity] = useState(() => {
    try {
      return localStorage.getItem('adnexus.tableDensity:roi') === 'compact' ? 'compact' : 'comfortable';
    } catch {
      return 'comfortable';
    }
  });
  const isNarrow = useMedia('(max-width: 768px)');
  const shareHydratedRef = useRef(false);
  const pendingShareFiltersRef = useRef(null);
  const skipPrefsSaveRef = useRef(true);
  const loadAbortRef = useRef(null);
  const breakdownAbortRef = useRef(null);
  const dataRef = useRef(data);
  const savedLoadKeyRef = useRef(saved?.loadKey || null);
  const skipLoadRef = useRef(Boolean(
    cacheFresh
    && saved?.data
    && saved?.loadKey
    && saved.loadKey === roiLoadKeyFromApplied(saved.applied || applied, user?.id)
  ));
  const staleSilentRoiRef = useRef(Boolean(saved?.data && saved?.fetchedAt && !cacheFresh));

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  useEffect(() => {
    try {
      localStorage.setItem('adnexus.tableDensity:roi', tableDensity);
    } catch {
      /* ignore */
    }
  }, [tableDensity]);

  useEffect(() => {
    skipPrefsSaveRef.current = true;
    const prefs = loadComparePrefs(user?.id);
    setCompareMode(prefs.mode);
    setCompareStart(prefs.startDate);
    setCompareEnd(prefs.endDate);
  }, [user?.id]);

  useEffect(() => {
    if (skipPrefsSaveRef.current) {
      skipPrefsSaveRef.current = false;
      return;
    }
    saveComparePrefs(user?.id, { mode: compareMode, startDate: compareStart, endDate: compareEnd });
  }, [user?.id, compareMode, compareStart, compareEnd]);

  useEffect(() => {
    if (shareHydratedRef.current) return;
    shareHydratedRef.current = true;

    const applyRange = (nextPreset, range, nextTarget, adsFilters = null) => {
      const tt = ['site', 'app', 'all'].includes(nextTarget) ? nextTarget : 'all';
      setPreset(nextPreset);
      setStartDate(range.startDate);
      setEndDate(range.endDate);
      setCountryPage(1);
      setApplied({
        startDate: range.startDate,
        endDate: range.endDate,
        targetType: tt,
        accountIds: adsFilters?.accountIds?.length ? adsFilters.accountIds : null,
        campaignIds: adsFilters?.campaignIds?.length ? adsFilters.campaignIds : null,
        appKeys: adsFilters?.appKeys?.length ? adsFilters.appKeys : null,
        siteKeys: adsFilters?.siteKeys?.length ? adsFilters.siteKeys : null,
        countryCodes: adsFilters?.countryCodes?.length ? adsFilters.countryCodes : null,
      });
    };

    const shared = parseReportShare(searchParams);
    if (shared) {
      const adsFilters = {
        accountIds: shared.accountIds || [],
        campaignIds: shared.campaignIds || [],
        appKeys: shared.appKeys || [],
        siteKeys: shared.siteKeys || [],
        countryCodes: shared.countryCodes || [],
      };
      if (
        adsFilters.accountIds.length
        || adsFilters.campaignIds.length
        || adsFilters.appKeys.length
        || adsFilters.siteKeys.length
        || adsFilters.countryCodes.length
      ) {
        pendingShareFiltersRef.current = adsFilters;
      }
      if (shared.preset && shared.preset !== 'custom') {
        const r = clampPresetRange(shared.preset, dateRestriction);
        if (r) applyRange(shared.preset, r, shared.targetType || 'all', adsFilters);
        return;
      }
      if (shared.startDate && shared.endDate) {
        const r = clampDateRange(shared.startDate, shared.endDate, dateRestriction);
        applyRange(shared.preset || 'custom', r, shared.targetType || 'all', adsFilters);
        return;
      }
      if (shared.targetType) {
        setApplied((prev) => ({ ...prev, targetType: shared.targetType }));
      }
      return;
    }

    const last = getLastPageFilters(LAST_FILTER_PAGES.roi, user?.id);
    if (last?.startDate && last?.endDate) {
      if (last.preset && last.preset !== 'custom') {
        const r = clampPresetRange(last.preset, dateRestriction);
        if (r) {
          applyRange(last.preset, r, last.targetType || 'all');
          return;
        }
      }
      const r = clampDateRange(last.startDate, last.endDate, dateRestriction);
      applyRange(last.preset || 'custom', r, last.targetType || 'all');
    }
  }, [searchParams, user?.id, dateRestriction]);

  const customDatesIncomplete = isCustomRangeIncomplete(preset, startDate, endDate);
  const presetLabel = useMemo(
    () => DATE_PRESETS.find((p) => p.id === preset)?.label || 'Custom',
    [preset]
  );

  const siteOptions = useMemo(() => {
    const seen = new Set();
    const opts = [];
    const isSubdomainHost = (raw) => {
      const host = String(raw || '').trim().toLowerCase().replace(/^www\./, '');
      // Any real host: a subdomain (finance2.arenapro6.com) or a site on the main domain itself (arenapro6.com).
      return Boolean(host) && host.includes('.');
    };
    const add = (value, label) => {
      const id = String(value || '').trim().toLowerCase().replace(/^www\./, '');
      if (!id || seen.has(id) || !isSubdomainHost(id)) return;
      seen.add(id);
      opts.push({ value: id, label: String(label || value).trim().replace(/^www\./i, '') || id });
    };
    // Prefer GAM subdomains from /roi-sites + inventory picker (never apex domains).
    (roiSiteOptions || []).forEach((o) => add(o.value, o.label));
    (siteHosts || []).forEach((h) => add(h, h));
    opts.sort((a, b) => a.label.localeCompare(b.label));
    return opts;
  }, [roiSiteOptions, siteHosts]);

  // Apps the user picked, and a way for Firebase to add / take back detected subdomains.
  const firebaseAppKeys = useMemo(
    // Apps the user picked. "All selected" only counts when the list is small (e.g. the account has one app);
    // with many apps it would link every subdomain, so detection waits for a specific pick.
    () => {
      const picked = collapseFullSelection(filterAppKeys, roiAppOptions);
      const apps = picked.length
        ? picked
        : (roiAppOptions.length <= FIREBASE_AUTO_ALL_MAX_APPS ? optionValues(roiAppOptions) : []);
      return apps.map((v) => String(v).toLowerCase());
    },
    [filterAppKeys, roiAppOptions]
  );
  const addDetectedSites = useCallback((sites) => {
    setFilterSiteKeys((prev) => (isAllSelection(prev) ? prev : [...new Set([...(prev || []), ...sites])]));
  }, []);
  const removeDetectedSites = useCallback((sites) => {
    const drop = new Set(sites);
    setFilterSiteKeys((prev) => (isAllSelection(prev) ? prev : (prev || []).filter((k) => !drop.has(k))));
  }, []);
  const firebaseDetect = useFirebaseSiteDetect(firebaseAppKeys, addDetectedSites, removeDetectedSites, mergedNetworkIds);

  const roiAccountsWithSpendInRange = useMemo(
    () => roiAccountOptions.filter((o) => Number(o.spend) > 0).length,
    [roiAccountOptions]
  );

  const filterSummary = useMemo(() => {
    if (!applied?.startDate) return null;
    const range = applied.startDate === applied.endDate
      ? applied.startDate
      : `${applied.startDate} → ${applied.endDate}`;
    const bits = [range];
    if (applied.accountIds?.length) bits.push(`${applied.accountIds.length} account(s)`);
    if (applied.campaignIds?.length) bits.push(`${applied.campaignIds.length} campaign(s)`);
    if (applied.appKeys?.length) bits.push(`${applied.appKeys.length} app(s)`);
    if (applied.siteKeys?.length) bits.push(`${applied.siteKeys.length} subdomain(s)`);
    if (applied.countryCodes?.length) bits.push(`${applied.countryCodes.length} countr${applied.countryCodes.length === 1 ? 'y' : 'ies'}`);
    return bits.join(' · ');
  }, [applied]);

  const compareRange = useMemo(
    () => resolveCompareRange(
      compareMode,
      applied?.startDate || startDate,
      applied?.endDate || endDate,
      { startDate: compareStart, endDate: compareEnd }
    ),
    [compareMode, applied?.startDate, applied?.endDate, startDate, endDate, compareStart, compareEnd]
  );
  const compareLabel = compareLabelFor(compareMode, compareRange);

  const persistLastFilters = useCallback((snap) => {
    saveLastPageFilters(LAST_FILTER_PAGES.roi, {
      preset: snap.preset,
      startDate: snap.startDate,
      endDate: snap.endDate,
      targetType: snap.targetType || 'all',
    }, user?.id);
  }, [user?.id]);

  const resolveFilterIds = useCallback((
    accountSel,
    campaignSel,
    appSel,
    siteSel,
    countrySel,
    accountOpts,
    campaignOpts,
    appOpts,
    siteOpts,
    countryOpts
  ) => {
    const accountIds = collapseFullSelection(accountSel, accountOpts);
    const campaignIds = collapseFullSelection(campaignSel, campaignOpts);
    const countryCodes = collapseFullSelection(countrySel, countryOpts);
    const appKeys = collapseFullSelection(appSel, appOpts);
    // Sites: manual GAM inventory — only send explicit picks (never auto-expand to all sites).
    const siteKeys = isAllSelection(siteSel)
      ? []
      : (Array.isArray(siteSel) ? siteSel.filter((v) => v && v !== ALL_SENTINEL) : []);
    // Always use targetType=all when filtering — app + site are a union (selecting
    // sites must not hide Ads app packages / earn).
    const targetType = 'all';
    return {
      accountIds: accountIds.length ? accountIds : null,
      campaignIds: campaignIds.length ? campaignIds : null,
      appKeys: appKeys.length ? appKeys : null,
      siteKeys: siteKeys.length ? siteKeys : null,
      countryCodes: countryCodes.length ? countryCodes : null,
      targetType,
    };
  }, []);

  const loadKey = useMemo(
    () => roiLoadKeyFromApplied(applied, user?.id),
    [
      applied?.startDate,
      applied?.endDate,
      applied?.targetType,
      applied?.accountIds,
      applied?.campaignIds,
      applied?.appKeys,
      applied?.siteKeys,
      applied?.countryCodes,
      user?.id,
    ]
  );

  const load = useCallback(async (range = applied, { silent = false } = {}) => {
    if (!range?.startDate || !range?.endDate) return;
    if (loadAbortRef.current) loadAbortRef.current.abort();
    if (breakdownAbortRef.current) breakdownAbortRef.current.abort();

    const controller = new AbortController();
    const bdController = new AbortController();
    loadAbortRef.current = controller;
    breakdownAbortRef.current = bdController;

    const hasCached = Boolean(dataRef.current?.summary)
      || Boolean(dataRef.current?.countryTargetBreakdown?.length)
      || Boolean(dataRef.current?.countryBreakdown?.length);
    if (!silent) {
      // A new filter / range: the numbers on screen belong to the previous one, so drop them and show loading
      // (the responses used to be merged onto them, which looked like fresh data while it was still loading).
      if (hasCached) {
        setData(null);
        setThresholdBanners([]);
      }
      setPriorSummary(null);
      setLoading(true);
      setBreakdownLoading(true);
    }
    setError(null);

    const buildParams = (extra = {}) => {
      const params = {
        start: range.startDate,
        end: range.endDate,
        targetType: range.targetType || 'all',
        ...extra,
      };
      if (range.accountIds?.length) params.accountIds = range.accountIds.join(',');
      if (range.campaignIds?.length) params.campaignIds = range.campaignIds.join(',');
      if (range.appKeys?.length) params.appKeys = range.appKeys.join(',');
      if (range.siteKeys?.length) params.siteKeys = range.siteKeys.join(',');
      if (range.countryCodes?.length) params.countryCodes = range.countryCodes.join(',');
      return params;
    };

    const summaryParams = buildParams({ summaryOnly: '1' });
    const breakdownParams = buildParams({ breakdownOnly: '1' });

    let summaryOk = false;
    let breakdownOk = false;
    let summaryErr = null;
    let breakdownErr = null;

    const summaryPromise = roiAPI.summary(summaryParams, { signal: controller.signal })
      .then((fast) => {
        if (controller.signal.aborted) return null;
        summaryOk = true;
        setData((prev) => mergeRoiSummaryPayload(prev, fast));
        setLastUpdated(nowTimeInTZ());
        setFetchedAt(Date.now());
        setEarnTz(fast?.earnTimezone || null);
        setThresholdBanners(evaluateRoiThresholds(fast?.summary || fast || {}));
        setLoading(false);
        return fast;
      })
      .catch((err) => {
        if (controller.signal.aborted || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') {
          return null;
        }
        summaryErr = err;
        logErrorForDebug(err, 'ROI summary');
        setLoading(false);
        return null;
      });

    const breakdownPromise = roiAPI.summary(breakdownParams, { signal: bdController.signal })
      .then((breakdown) => {
        if (bdController.signal.aborted) return null;
        breakdownOk = true;
        setData((prev) => mergeRoiBreakdownPayload(prev, breakdown));
        setBreakdownLoading(false);
        // Overview can paint from table totals immediately.
        setLoading(false);
        setFetchedAt(Date.now());
        return breakdown;
      })
      .catch((err) => {
        if (bdController.signal.aborted || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') {
          return null;
        }
        breakdownErr = err;
        logErrorForDebug(err, 'ROI breakdown');
        setBreakdownLoading(false);
        return null;
      });

    try {
      await Promise.all([summaryPromise, breakdownPromise]);
      if (controller.signal.aborted && bdController.signal.aborted) return;
      if (!summaryOk && !breakdownOk) {
        setError(getUserFacingMessage(
          summaryErr || breakdownErr,
          'Could not load ROI summary.'
        ));
        if (!hasCached || !silent) {
          setData(null);
          setThresholdBanners([]);
        }
      } else if (!summaryOk && summaryErr) {
        setError(getUserFacingMessage(summaryErr, 'Could not load ROI overview cards.'));
      } else {
        setError(null);
      }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
      if (!bdController.signal.aborted) setBreakdownLoading(false);
    }
  }, [applied]);

  useEffect(() => {
    if (skipLoadRef.current && loadKey === savedLoadKeyRef.current) {
      skipLoadRef.current = false;
      setLoading(false);
      setBreakdownLoading(false);
      return;
    }
    skipLoadRef.current = false;
    if (staleSilentRoiRef.current && dataRef.current) {
      staleSilentRoiRef.current = false;
      load(applied, { silent: true });
      return;
    }
    load();
  }, [loadKey, load, applied]);

  useEffect(() => {
    if (!data) return;
    dispatch(saveReportPage({
      pageKey: 'roi',
      payload: {
        userId: user?.id,
        clientId: user?.clientId || null,
        data: slimRoiForCache(data),
        applied,
        loadKey,
        fetchedAt: fetchedAt || Date.now(),
        lastUpdated,
        preset,
        startDate,
        endDate,
        filterAccountIds,
        filterCampaignIds,
        filterAppKeys,
        filterSiteKeys,
        filterCountryCodes,
        roiAccountOptions,
        roiCampaignOptions,
        roiAppOptions,
        roiCountryOptions,
        roiSiteOptions,
      },
    }));
  }, [
    dispatch, user?.id, data, applied, loadKey, fetchedAt, lastUpdated,
    preset, startDate, endDate,
    filterAccountIds, filterCampaignIds, filterAppKeys, filterSiteKeys, filterCountryCodes,
    roiAccountOptions, roiCampaignOptions, roiAppOptions, roiCountryOptions, roiSiteOptions,
  ]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const health = await adsAPI.syncHealth();
        if (!cancelled) setAdsSyncHealth(health);
      } catch {
        if (!cancelled) setAdsSyncHealth(null);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id, lastUpdated]);

  // Clear ROI payload when switching users so stale overview/table never linger.
  const prevRoiUserIdRef = useRef(user?.id);
  useEffect(() => {
    if (prevRoiUserIdRef.current === user?.id) return;
    prevRoiUserIdRef.current = user?.id;
    setData(null);
    setPriorSummary(null);
    setRoiAccountOptions([]);
    setRoiCampaignOptions([]);
    setRoiAppOptions([]);
    setRoiCountryOptions([]);
    setRoiSiteOptions([]);
    setError(null);
    setThresholdBanners([]);
    setLoading(false);
    setBreakdownLoading(false);
    setFetchedAt(null);
    dispatch(saveReportPage({ pageKey: 'roi', payload: null }));
  }, [user?.id, dispatch]);

  useEffect(() => {
    setCountryPage(1);
    setCountrySearch('');
  }, [
    applied?.startDate,
    applied?.endDate,
    applied?.accountIds,
    applied?.campaignIds,
    applied?.appKeys,
    applied?.siteKeys,
    applied?.countryCodes,
  ]);

  // Load Ads accounts for filter as soon as dates are applied (do not wait on summary).
  useEffect(() => {
    const start = applied?.startDate;
    const end = applied?.endDate;
    if (!start || !end) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await adsAPI.listRoiAccounts({ start, end });
        if (cancelled) return;
        const opts = (res.accounts || [])
          .map((a) => ({
          value: a.id,
          label: a.descriptiveName && a.customerId && a.descriptiveName !== a.customerId
            ? `${a.descriptiveName} (${a.customerId})`
            : (a.descriptiveName || a.customerId || a.id),
          spend: Number(a.spend) || 0,
        }));
        setRoiAccountOptions(opts);
        setFilterAccountIds((prev) => {
          const pending = pendingShareFiltersRef.current;
          if (pending?.accountIds?.length) {
            const allowed = new Set(opts.map((o) => o.value));
            const kept = pending.accountIds.filter((id) => allowed.has(id));
            pendingShareFiltersRef.current = { ...pending, accountIds: [] };
            if (kept.length) return kept;
          }
          if (!opts.length) return [];
          if (isAllSelection(prev) || !prev?.length) return toAllSelection();
          const allowed = new Set(opts.map((o) => o.value));
          const kept = prev.filter((id) => id !== ALL_SENTINEL && allowed.has(id));
          return kept.length ? kept : toAllSelection();
        });
      } catch (err) {
        logErrorForDebug(err, 'ROI accounts filter');
        if (!cancelled) setRoiAccountOptions([]);
      }
    })();
    return () => { cancelled = true; };
  }, [applied?.startDate, applied?.endDate]);

  // Load campaigns for selected accounts
  useEffect(() => {
    const start = applied?.startDate;
    const end = applied?.endDate;
    if (!start || !end) return undefined;
    let cancelled = false;
    (async () => {
      setRoiCampaignsLoading(true);
      try {
        const accountIds = collapseFullSelection(filterAccountIds, roiAccountOptions);
        const params = { start, end };
        if (accountIds.length) params.accountIds = accountIds.join(',');
        const res = await adsAPI.listRoiCampaigns(params);
        if (cancelled) return;
        const opts = (res.campaigns || []).map((c) => ({
          value: c.campaignId,
          label: c.campaignName
            ? `${c.campaignName}${c.accountName ? ` · ${c.accountName}` : ''}`
            : c.campaignId,
          campaignName: c.campaignName,
          adsAccountId: c.adsAccountId,
        }));
        setRoiCampaignOptions(opts);
        setRoiCampaignsFallback(Boolean(res.fallbackUsed));
        // Honor shared/preset campaign list once; otherwise select all for current accounts.
        setFilterCampaignIds(() => {
          const pending = pendingShareFiltersRef.current;
          if (pending?.campaignIds?.length) {
            const allowed = new Set(opts.map((o) => o.value));
            const kept = pending.campaignIds.filter((id) => allowed.has(id));
            pendingShareFiltersRef.current = { ...pending, campaignIds: [] };
            if (kept.length) return kept;
          }
          return opts.length ? toAllSelection() : [];
        });
      } catch (err) {
        logErrorForDebug(err, 'ROI campaigns filter');
        if (!cancelled) {
          setRoiCampaignOptions([]);
          setFilterCampaignIds([]);
          setRoiCampaignsFallback(false);
        }
      } finally {
        if (!cancelled) setRoiCampaignsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [applied?.startDate, applied?.endDate, filterAccountIds, roiAccountOptions]);

  // App IDs from Google Ads for the selected accounts/campaigns (App Campaign settings).
  useEffect(() => {
    const start = applied?.startDate;
    const end = applied?.endDate;
    if (!start || !end) return undefined;
    // Wait until campaigns for the current accounts have loaded so we do not
    // request apps with a stale campaign filter from a previous selection.
    if (roiCampaignsLoading) return undefined;
    let cancelled = false;
    (async () => {
      setRoiAppsLoading(true);
      try {
        const accountIds = collapseFullSelection(filterAccountIds, roiAccountOptions);
        const campaignIds = collapseFullSelection(filterCampaignIds, roiCampaignOptions);
        const params = { start, end };
        if (accountIds.length) params.accountIds = accountIds.join(',');
        // Only narrow by campaigns when the user unselected some; "all" → accounts only.
        if (campaignIds.length) params.campaignIds = campaignIds.join(',');
        const res = await adsAPI.listRoiRelatedTargets(params);
        if (cancelled) return;
        const opts = (res.apps || []).map((a) => ({
          value: a.id,
          label: a.label || a.id,
        }));
        setRoiAppOptions(opts);
        setFilterAppKeys(() => {
          const pending = pendingShareFiltersRef.current;
          if (pending?.appKeys?.length) {
            const allowed = new Set(opts.map((o) => o.value));
            const kept = pending.appKeys
              .map((k) => String(k).toLowerCase())
              .filter((id) => allowed.has(id));
            pendingShareFiltersRef.current = { ...pending, appKeys: [] };
            if (kept.length) return kept;
          }
          return opts.length ? toAllSelection() : [];
        });
      } catch (err) {
        logErrorForDebug(err, 'ROI related apps');
        if (!cancelled) {
          setRoiAppOptions([]);
          setFilterAppKeys([]);
        }
      } finally {
        if (!cancelled) setRoiAppsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [
    applied?.startDate,
    applied?.endDate,
    filterAccountIds,
    filterCampaignIds,
    roiAccountOptions,
    roiCampaignOptions,
    roiCampaignsLoading,
  ]);

  // Countries with Ads spend for selected accounts/campaigns.
  useEffect(() => {
    const start = applied?.startDate;
    const end = applied?.endDate;
    if (!start || !end) return undefined;
    if (roiCampaignsLoading) return undefined;
    let cancelled = false;
    (async () => {
      setRoiCountriesLoading(true);
      try {
        const accountIds = collapseFullSelection(filterAccountIds, roiAccountOptions);
        const campaignIds = collapseFullSelection(filterCampaignIds, roiCampaignOptions);
        const params = { start, end };
        if (accountIds.length) params.accountIds = accountIds.join(',');
        if (campaignIds.length) params.campaignIds = campaignIds.join(',');
        const res = await adsAPI.listRoiCountries(params);
        if (cancelled) return;
        const opts = (res.countries || []).map((c) => ({
          value: String(c.code || '').toUpperCase(),
          label: c.name ? `${c.name} (${c.code})` : String(c.code),
          spend: Number(c.spend) || 0,
        })).filter((o) => o.value);
        setRoiCountryOptions(opts);
        setRoiCountriesFallback(Boolean(res.fallbackUsed));
        setFilterCountryCodes(() => {
          const pending = pendingShareFiltersRef.current;
          if (pending?.countryCodes?.length) {
            const allowed = new Set(opts.map((o) => o.value));
            const kept = pending.countryCodes
              .map((k) => String(k).toUpperCase())
              .filter((id) => allowed.has(id));
            pendingShareFiltersRef.current = { ...pending, countryCodes: [] };
            if (kept.length) return kept;
          }
          return opts.length ? toAllSelection() : [];
        });
      } catch (err) {
        logErrorForDebug(err, 'ROI countries filter');
        if (!cancelled) {
          setRoiCountryOptions([]);
          setFilterCountryCodes([]);
          setRoiCountriesFallback(false);
        }
      } finally {
        if (!cancelled) setRoiCountriesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [
    applied?.startDate,
    applied?.endDate,
    filterAccountIds,
    filterCampaignIds,
    roiAccountOptions,
    roiCampaignOptions,
    roiCampaignsLoading,
  ]);

  useEffect(() => {
    const prior = compareRange;
    if (!prior || !isPeriodAllowed(prior, dateRestriction)) {
      setPriorSummary(null);
      return undefined;
    }
    if (loading) return undefined;
    let cancelled = false;
    setPriorSummary(null);
    (async () => {
      try {
        const params = {
          start: prior.startDate,
          end: prior.endDate,
          targetType: applied?.targetType || 'all',
          summaryOnly: '1',
        };
        if (applied?.accountIds?.length) params.accountIds = applied.accountIds.join(',');
        if (applied?.campaignIds?.length) params.campaignIds = applied.campaignIds.join(',');
        if (applied?.appKeys?.length) params.appKeys = applied.appKeys.join(',');
        if (applied?.siteKeys?.length) params.siteKeys = applied.siteKeys.join(',');
        if (applied?.countryCodes?.length) params.countryCodes = applied.countryCodes.join(',');
        const res = await roiAPI.summary(params);
        if (!cancelled) setPriorSummary(res?.summary || null);
      } catch (err) {
        logErrorForDebug(err, 'ROI compare summary');
        if (!cancelled) setPriorSummary(null);
      }
    })();
    return () => { cancelled = true; };
  }, [
    compareRange?.startDate,
    compareRange?.endDate,
    applied?.targetType,
    applied?.startDate,
    applied?.endDate,
    applied?.accountIds,
    applied?.campaignIds,
    applied?.appKeys,
    applied?.siteKeys,
    applied?.countryCodes,
    dateRestriction,
    loading,
  ]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setRoiSitesLoading(true);
      try {
        // A domain user with several networks is offered the assigned sites of all of them.
        const lists = mergedNetworkIds.length > 1
          ? await Promise.all(mergedNetworkIds.map((id) => adsAPI.listRoiSites(undefined, id).catch(() => null)))
          : [await adsAPI.listRoiSites()];
        if (cancelled) return;
        const res = { sites: lists.filter(Boolean).flatMap((r) => r.sites || []) };
        const opts = (res.sites || []).map((s) => ({
          value: String(s.id || '').toLowerCase(),
          label: s.label || s.id,
        })).filter((o) => o.value);
        setRoiSiteOptions(opts);
        setFilterSiteKeys((prev) => {
          const pending = pendingShareFiltersRef.current;
          if (pending?.siteKeys?.length) {
            const allowed = new Set(opts.map((o) => o.value));
            const kept = pending.siteKeys
              .map((k) => String(k).toLowerCase())
              .filter((id) => allowed.has(id) || isValidHost(id));
            pendingShareFiltersRef.current = { ...pending, siteKeys: [] };
            if (kept.length) return kept;
          }
          if (!prev?.length) return [];
          const allowed = new Set(opts.map((o) => o.value));
          // Hosts typed by hand are not in the loaded list; keep them (the server limits a domain user to their scope).
          const kept = prev.filter((id) => id === ALL_SENTINEL || allowed.has(id) || isValidHost(id));
          return kept.length ? kept : [];
        });
      } catch (err) {
        logErrorForDebug(err, 'ROI sites filter');
        if (!cancelled) setRoiSiteOptions([]);
      } finally {
        if (!cancelled) setRoiSitesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [mergedNetworkIds.join(",")]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setInventoryLoading(true);
      try {
        // A domain user with several networks sees them together, so the sites and apps of all of them can be picked.
        const picker = mergedNetworkIds.length > 1
          ? await usersAPI.getMergedInventoryPicker(mergedNetworkIds)
          : await usersAPI.getInventoryPicker();
        if (cancelled) return;
        setSiteHosts(Array.isArray(picker?.siteHosts) ? picker.siteHosts : []);
        setAppIds(Array.isArray(picker?.appIds) ? picker.appIds : []);
      } catch (err) {
        logErrorForDebug(err, 'ROI inventory picker');
        if (!cancelled) {
          setSiteHosts([]);
          setAppIds([]);
        }
      } finally {
        if (!cancelled) setInventoryLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [mergedNetworkIds.join(',')]);

  const getPresetSnapshot = useCallback(() => filtersOnlySnapshot({
    targetType: applied?.targetType || 'all',
    accountIds: filterAccountIds?.length ? filterAccountIds : toAllSelection(),
    campaignIds: filterCampaignIds?.length ? filterCampaignIds : toAllSelection(),
    appKeys: filterAppKeys?.length ? filterAppKeys : [],
    siteKeys: filterSiteKeys?.length ? filterSiteKeys : [],
    countryCodes: filterCountryCodes?.length ? filterCountryCodes : toAllSelection(),
    accountLabels: labelsForSelection(filterAccountIds?.length ? filterAccountIds : toAllSelection(), roiAccountOptions),
    campaignLabels: labelsForSelection(filterCampaignIds?.length ? filterCampaignIds : toAllSelection(), roiCampaignOptions),
    appLabels: labelsForSelection(filterAppKeys, roiAppOptions),
    siteLabels: labelsForSelection(filterSiteKeys, siteOptions),
    countryLabels: labelsForSelection(filterCountryCodes?.length ? filterCountryCodes : toAllSelection(), roiCountryOptions),
  }), [
    applied?.targetType,
    filterAccountIds,
    filterCampaignIds,
    filterAppKeys,
    filterSiteKeys,
    filterCountryCodes,
    roiAccountOptions,
    roiCampaignOptions,
    roiAppOptions,
    siteOptions,
    roiCountryOptions,
  ]);

  const handleCopyLink = async () => {
    await copyReportLink({
      ...getPresetSnapshot(),
      preset,
      startDate: applied?.startDate || startDate,
      endDate: applied?.endDate || endDate,
    });
    showToast({ message: 'Link copied — opens this exact ROI view' });
  };

  const handleCompareMode = (mode) => {
    setCompareMode(mode);
    if (mode === 'custom' && (!compareStart || !compareEnd)) {
      const prior = previousPeriodRange(applied?.startDate || startDate, applied?.endDate || endDate);
      if (prior) {
        setCompareStart(prior.startDate);
        setCompareEnd(prior.endDate);
      }
    }
  };

  const dismissRoiTip = () => {
    try {
      localStorage.setItem(ROI_TIP_KEY, 'done');
    } catch {
      /* ignore */
    }
    setShowRoiTip(false);
  };

  const onPreset = (p) => {
    if (dateFilterLocked) return;
    setPreset(p);
    if (p !== 'custom') {
      const r = clampPresetRange(p, dateRestriction);
      if (r) {
        setStartDate(r.startDate);
        setEndDate(r.endDate);
      }
    }
  };

  const applyPreset = (p) => {
    if (dateFilterLocked) return;
    const r = clampPresetRange(p, dateRestriction);
    if (!r) return;
    setPreset(p);
    setStartDate(r.startDate);
    setEndDate(r.endDate);
    setCountryPage(1);
    const ids = resolveFilterIds(
      filterAccountIds,
      filterCampaignIds,
      filterAppKeys,
      filterSiteKeys,
      filterCountryCodes,
      roiAccountOptions,
      roiCampaignOptions,
      roiAppOptions,
      siteOptions,
      roiCountryOptions
    );
    const next = { startDate: r.startDate, endDate: r.endDate, ...ids };
    setApplied(next);
    persistLastFilters({
      preset: p,
      startDate: r.startDate,
      endDate: r.endDate,
      targetType: ids.targetType,
    });
    load(next);
  };

  const applyFilter = () => {
    if (customDatesIncomplete) return;
    const ids = resolveFilterIds(
      filterAccountIds,
      filterCampaignIds,
      filterAppKeys,
      filterSiteKeys,
      filterCountryCodes,
      roiAccountOptions,
      roiCampaignOptions,
      roiAppOptions,
      siteOptions,
      roiCountryOptions
    );
    const next = {
      startDate,
      endDate,
      ...ids,
    };
    setCountryPage(1);
    setApplied(next);
    persistLastFilters({ preset, startDate, endDate, targetType: ids.targetType });
    load(next);
  };

  const reset = () => {
    const r = clampPresetRange('today', dateRestriction) || todayInit;
    setPreset('today');
    setStartDate(r.startDate);
    setEndDate(r.endDate);
    setFilterAccountIds(toAllSelection());
    setFilterCampaignIds(toAllSelection());
    setFilterAppKeys(toAllSelection());
    setFilterSiteKeys([]);
    setFilterCountryCodes(toAllSelection());
    setCountryPage(1);
    setCountrySearch('');
    const next = {
      startDate: r.startDate,
      endDate: r.endDate,
      targetType: 'all',
      accountIds: null,
      campaignIds: null,
      appKeys: null,
      siteKeys: null,
      countryCodes: null,
    };
    setApplied(next);
    persistLastFilters({
      preset: 'today',
      startDate: r.startDate,
      endDate: r.endDate,
      targetType: 'all',
    });
    load(next);
  };

  const openExpenseForm = () => {
    setExpenseSharedDate(applied?.endDate || endDate);
    setExpense1(emptyExpenseSlot());
    setExpense2(emptyExpenseSlot());
    setShowExpense(true);
  };

  const updateExpenseSlot = (setSlot, patch) => {
    setSlot((f) => {
      const next = { ...f, ...patch };
      if (patch.targetType && patch.targetType !== f.targetType) {
        next.targetKey = '';
      }
      return next;
    });
  };

  const targetOptionsFor = (type) => {
    const seen = new Set();
    const options = [];
    const add = (raw) => {
      const label = String(raw || '').trim();
      if (!label) return;
      const id = label.toLowerCase();
      if (seen.has(id)) return;
      seen.add(id);
      options.push({ id, label });
    };
    if (type === 'site') {
      (siteHosts || []).forEach(add);
      (siteOptions || []).forEach((o) => add(o.value));
    } else if (type === 'app') {
      (appIds || []).forEach(add);
      (roiAppOptions || []).forEach((o) => add(o.value));
    }
    options.sort((a, b) => a.label.localeCompare(b.label));
    return options;
  };

  const saveExpenses = async (e) => {
    e.preventDefault();
    const slots = [expense1, expense2]
      .map((slot) => ({
        ...slot,
        expenseDate: expenseSharedDate || applied?.endDate || endDate,
        amount: Number(slot.amount),
        targetKey: String(slot.targetKey || '').trim().toLowerCase(),
      }))
      .filter((slot) => Number.isFinite(slot.amount) && slot.amount > 0);

    if (!slots.length) {
      setError('Enter an amount for at least one expense.');
      return;
    }

    setExpenseBusy(true);
    setError(null);
    try {
      for (const slot of slots) {
        if (slot.targetType !== 'general' && !slot.targetKey) {
          throw new Error(
            slot.targetType === 'site'
              ? 'Select a site host for site expenses.'
              : 'Select an app ID / package for app expenses.'
          );
        }
        await adsAPI.createExpense(slot);
      }
      setShowExpense(false);
      setExpense1(emptyExpenseSlot());
      setExpense2(emptyExpenseSlot());
      await load();
    } catch (err) {
      setError(getUserFacingMessage(err, 'Could not save expenses.'));
    } finally {
      setExpenseBusy(false);
    }
  };

  const deleteExpense = async (id) => {
    const removed = (data?.expenses || data?.generalExpenses || []).find((e) => e.id === id);
    // Optimistic UI — remove immediately so the row does not linger until reload/cache.
    setData((prev) => {
      if (!prev) return prev;
      const expenses = (prev.expenses || []).filter((e) => e.id !== id);
      const generalExpenses = (prev.generalExpenses || []).filter((e) => e.id !== id);
      let summary = prev.summary;
      if (summary && removed && (removed.targetType === 'general' || !removed.targetType)) {
        const otherExpenses = Math.max(
          0,
          Math.round(((Number(summary.otherExpenses) || 0) - (Number(removed.amount) || 0)) * 100) / 100
        );
        const earn = Number(summary.earn) || 0;
        const adsSpend = Number(summary.adsSpend) || 0;
        const profitExpense = Math.round((earn - otherExpenses) * 100) / 100;
        const totalCost = Math.round((adsSpend + otherExpenses) * 100) / 100;
        const roiExpensePercent = otherExpenses > 0
          ? Math.round(((earn - otherExpenses) / otherExpenses) * 10000) / 100
          : null;
        const roiPercent = totalCost > 0
          ? Math.round(((earn - totalCost) / totalCost) * 10000) / 100
          : null;
        summary = {
          ...summary,
          otherExpenses,
          profitExpense,
          totalCost,
          profit: Math.round((earn - totalCost) * 100) / 100,
          roiExpensePercent,
          roiPercent,
        };
      }
      return { ...prev, expenses, generalExpenses, summary };
    });
    try {
      await adsAPI.deleteExpense(id);
      await load();
    } catch (err) {
      setError(getUserFacingMessage(err, 'Could not delete expense.'));
      await load();
    }
  };

  const renderExpenseFields = (slot, setSlot, title) => {
    const options = targetOptionsFor(slot.targetType);
    return (
      <div className="filter-field">
        <div className="filter-section-head" style={{ marginBottom: 8 }}>
          <span className="filter-section-title">{title}</span>
        </div>
        <label className="ui-field">
          <span className="ui-field-label">Amount</span>
          <input
            className="ui-field-input"
            type="number"
            min="0"
            step="0.01"
            value={slot.amount}
            onChange={(e) => updateExpenseSlot(setSlot, { amount: e.target.value })}
            placeholder="0.00"
          />
        </label>
        <label className="ui-field">
          <span className="ui-field-label">Label</span>
          <input
            className="ui-field-input"
            type="text"
            value={slot.label}
            onChange={(e) => updateExpenseSlot(setSlot, { label: e.target.value })}
            placeholder="Creative, tools, salary…"
          />
        </label>
        <label className="ui-field">
          <span className="ui-field-label">Attach to</span>
          <select
            className="ui-field-input"
            value={slot.targetType}
            onChange={(e) => updateExpenseSlot(setSlot, { targetType: e.target.value })}
          >
            <option value="general">General (all inventory)</option>
            <option value="site">Site</option>
            <option value="app">App ID</option>
          </select>
        </label>
        {slot.targetType !== 'general' && (
          <label className="ui-field">
            <span className="ui-field-label">
              {slot.targetType === 'site' ? 'Site host' : 'App ID / package'}
            </span>
            {options.length > 0 ? (
              <select
                className="ui-field-input"
                value={slot.targetKey}
                onChange={(e) => updateExpenseSlot(setSlot, { targetKey: e.target.value })}
                required={Number(slot.amount) > 0}
              >
                <option value="">
                  {inventoryLoading ? 'Loading…' : 'Select…'}
                </option>
                {options.map((o) => (
                  <option key={o.id} value={o.id}>{o.label}</option>
                ))}
              </select>
            ) : (
              <input
                className="ui-field-input"
                type="text"
                value={slot.targetKey}
                onChange={(e) => updateExpenseSlot(setSlot, { targetKey: e.target.value })}
                placeholder={slot.targetType === 'site' ? 'example.com' : 'com.example.app'}
                required={Number(slot.amount) > 0}
              />
            )}
          </label>
        )}
      </div>
    );
  };

  const summary = data?.summary || {};
  const spendCurrency = summary.adsSpendCurrency || data?.spendCurrency || 'USD';
  const overviewReady = Boolean(summary && Object.keys(summary).length > 0);

  const cardDeltas = useMemo(() => {
    if (!priorSummary) return {};
    return {
      adsSpend: pctChange(summary.adsSpend, priorSummary.adsSpend),
      earn: pctChange(summary.earn, priorSummary.earn),
      roiSpendPercent: pctChange(summary.roiSpendPercent, priorSummary.roiSpendPercent),
    };
  }, [summary, priorSummary]);

  const countryTargetBreakdown = data?.countryTargetBreakdown || [];
  const countryBreakdown = data?.countryBreakdown || [];
  const countryTargetDailyBreakdown = data?.countryTargetDailyBreakdown || [];
  const singleAccountMode = (applied?.accountIds || []).length === 1;
  // Admin: turn the selected Ads account into a sheet (sidebar link under ROI with its own day / week / month / year view).
  useRoiDisplayCurrency();
  const roiSheets = useRoiSheets(user?.id);
  // "All" collapses to no account filter, so a user who can see exactly one account (a domain user's usual case) has
  // none selected explicitly; that one account is still the sheet's account.
  const soleAccountOption = !(applied?.accountIds || []).length && roiAccountOptions.length === 1
    ? String(roiAccountOptions[0].value) : '';
  const sheetAccount = singleAccountMode ? String(applied.accountIds[0]) : soleAccountOption;
  const existingSheet = sheetAccount ? roiSheets.find((sh) => sheetAccountId(sh) === sheetAccount) : null;
  const addSheet = () => {
    if (!sheetAccount) return;
    const opt = roiAccountOptions.find((o) => String(o.value) === sheetAccount);
    addRoiSheet({ id: sheetAccount, label: opt?.label || sheetAccount }, user?.id);
  };
  const countryTree = useMemo(
    () => buildCountryTree(
      countryBreakdown,
      countryTargetBreakdown,
      countryTargetDailyBreakdown,
      {
        startDate: applied?.startDate || startDate,
        endDate: applied?.endDate || endDate,
        singleAccountMode,
      },
    ),
    [
      countryBreakdown,
      countryTargetBreakdown,
      countryTargetDailyBreakdown,
      applied?.startDate,
      applied?.endDate,
      startDate,
      endDate,
      singleAccountMode,
    ]
  );
  const inventoryEarn = useMemo(
    () => sumInventoryEarnFromBreakdown(countryTargetBreakdown),
    [countryTargetBreakdown]
  );

  return (
    <div className="dashboard-page reporting-page roi-page">
      <PageHeader
        title="ROI"
        subtitle="Ads spend and other expenses vs GAM earn — separate ROI% for spend and expenses"
        summary={filterSummary}
      >
        <RoiCurrencySelect />
        <button type="button" className="btn-outline-action" onClick={openExpenseForm}>
          Add expenses
        </button>
        {sheetAccount ? (
          existingSheet ? (
            <Link className="btn-outline-action" to={`/roi/sheet/${existingSheet.id}`} title="This account already has a sheet in the sidebar">
              Open sheet
            </Link>
          ) : (
            <button type="button" className="btn-outline-action" onClick={addSheet} title="Add this Ads account as a sheet under ROI in the sidebar">
              Add sheet
            </button>
          )
        ) : null}
        <button type="button" className="btn-outline-action" onClick={handleCopyLink}>
          Copy link
        </button>
        <SavePresetButton
          page={PRESET_PAGES.roi}
          userId={user?.id}
          getSnapshot={getPresetSnapshot}
          variant="primary"
          hint="Saves accounts, campaigns, app IDs, sites, and countries (not the date range)."
        />
      </PageHeader>
      <RoiTimezoneNote info={earnTz} />

      <CompareRangeBar
        mode={compareMode}
        onModeChange={handleCompareMode}
        customStart={compareStart}
        customEnd={compareEnd}
        onCustomStart={(v) => setCompareStart(clampDateValue(v, dateRestriction))}
        onCustomEnd={(v) => setCompareEnd(clampDateValue(v, dateRestriction))}
        minDate={dateRestriction?.startDate}
        maxDate={dateRestriction?.endDate}
      />

      {showRoiTip && (
        <p className="form-note page-restriction-note" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <span>
            Tip: Pick a date range, Apply Filter, then use Compare to see earn / spend deltas. Copy link or Save preset to reuse this view.
          </span>
          <button type="button" className="btn-reset" onClick={dismissRoiTip} aria-label="Dismiss tip">
            Dismiss
          </button>
        </p>
      )}

      {dateRestriction && (
        <p className="form-note page-restriction-note">
          {dateFilterLocked
            ? `Data locked to: ${formatDateRestrictionLabel(dateRestriction)}`
            : `Allowed filter window: ${formatDateRestrictionLabel(dateRestriction)}`}
        </p>
      )}

      <ThresholdAlertBanner
        items={thresholdBanners}
        onDismiss={(id) => setThresholdBanners((prev) => prev.filter((b) => b.id !== id))}
      />

      <div className={`filter-card gam-report-shell ${filtersOpen ? 'filter-card-open' : ''}`}>
        <div className="filter-card-head filter-card-head-sticky">
          <button
            type="button"
            className="filter-card-title filter-card-toggle"
            onClick={() => setFiltersOpen((v) => !v)}
            aria-expanded={filtersOpen}
          >
            ROI filters {filtersOpen ? '▾' : '▸'}
          </button>
          <div className="filter-actions filter-actions--desktop">
            <button
              type="button"
              className="btn-generate"
              onClick={applyFilter}
              disabled={customDatesIncomplete}
              title={customDatesIncomplete ? 'Select both start and end dates' : ''}
            >
              ✓ Apply Filter
            </button>
            <button type="button" className="btn-reset" onClick={reset}>↺ Reset</button>
          </div>
        </div>

        {filtersOpen && (
          <div className="gam-report-settings">
            <RoiFilterSection
              title="Date range"
              subtitle="Same presets as Dashboard and Reporting"
            >
              <RoiFilterRow icon="calendar" label="Period">
                <div className="roi-filter-date-toolbar">
                  <div className="dash-date-display">
                    <span className="dash-date-label">{presetLabel}</span>
                    <span className="dash-date-range">
                      {customDatesIncomplete
                        ? 'Select start & end dates'
                        : (startDate && endDate
                          ? (startDate !== endDate ? `${startDate} → ${endDate}` : startDate)
                          : '…')}
                    </span>
                  </div>
                  {!dateFilterLocked && presetOptions.length > 0 && (
                    <div className="preset-pills roi-filter-date-pills">
                      {presetOptions.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          className={`preset-pill ${preset === p.id ? 'active' : ''}`}
                          onClick={() => onPreset(p.id)}
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </RoiFilterRow>
              {!dateFilterLocked && preset === 'custom' && (
                <div className="roi-filter-date-custom">
                  <div className="filter-field">
                    <label>Start date</label>
                    <input
                      type="date"
                      value={startDate || ''}
                      min={dateRestriction?.startDate || undefined}
                      max={dateRestriction?.endDate || undefined}
                      onChange={(e) => setStartDate(clampDateValue(e.target.value, dateRestriction))}
                    />
                  </div>
                  <div className="filter-field">
                    <label>End date</label>
                    <input
                      type="date"
                      value={endDate || ''}
                      min={dateRestriction?.startDate || undefined}
                      max={dateRestriction?.endDate || undefined}
                      onChange={(e) => setEndDate(clampDateValue(e.target.value, dateRestriction))}
                    />
                  </div>
                </div>
              )}
            </RoiFilterSection>

            <RoiFilterSection
              title="Google Ads & inventory"
              subtitle="Accounts/campaigns/apps from Ads; Subdomains are independent — pick GAM subdomains to add site revenue (INR)."
            >
              <RoiFilterRow icon="accounts" label="Ads accounts">
                <MultiSelect
                  options={roiAccountOptions}
                  value={filterAccountIds}
                  onChange={setFilterAccountIds}
                  placeholder={
                    roiAccountOptions.length
                      ? 'Search Ads accounts…'
                      : 'No Ads accounts found — connect accounts under Admin → Google Ads accounts'
                  }
                  searchable
                  showSelectAll
                  selectAllLabel="Select all accounts"
                />
              </RoiFilterRow>
              <RoiFilterRow icon="campaigns" label="Campaigns">
                <MultiSelect
                  options={roiCampaignOptions}
                  value={filterCampaignIds}
                  onChange={setFilterCampaignIds}
                  placeholder="Paste campaign names, Enter to select…"
                  disabled={!roiCampaignOptions.length && !roiCampaignsLoading}
                  loading={roiCampaignsLoading}
                  searchable
                  showSelectAll
                  selectAllLabel="Select all campaigns"
                />
              </RoiFilterRow>
              <RoiFilterRow icon="apps" label="App IDs">
                <MultiSelect
                  options={roiAppOptions}
                  value={filterAppKeys}
                  onChange={setFilterAppKeys}
                  placeholder={
                    roiAppsLoading
                      ? 'Loading apps from Ads…'
                      : (roiAppOptions.length
                        ? 'Apps from selected accounts/campaigns…'
                        : 'No App Campaign package IDs found…')
                  }
                  disabled={!roiAppOptions.length && !roiAppsLoading}
                  loading={roiAppsLoading}
                  searchable
                  showSelectAll
                  selectAllLabel="Select all related apps"
                />
              </RoiFilterRow>
              <RoiFilterRow icon="sites" label="Subdomains">
                <MultiSelect
                  options={siteOptions}
                  value={filterSiteKeys}
                  onChange={setFilterSiteKeys}
                  placeholder={
                    roiSitesLoading
                      ? 'Loading GAM subdomains…'
                      : (siteOptions.length
                        ? 'Pick or type subdomains → Apply Filter to load their revenue (₹)…'
                        : 'Type a subdomain, e.g. finance2.example.com')
                  }
                  loading={roiSitesLoading}
                  searchable
                  showSelectAll
                  selectAllLabel="Select all subdomains"
                  allowCustom
                  normalizeCustom={normalizeHostInput}
                  validateCustom={isValidHost}
                />
              </RoiFilterRow>
              {firebaseDetect.notInGam?.length > 0
                && collapseFullSelection(filterAccountIds, roiAccountOptions).length === 1
                && !isAllSelection(filterSiteKeys) && (
                <div className="roi-filter-hint roi-filter-hint-warn" role="alert">
                  Not in the GAM network (no revenue to show):{' '}
                  {firebaseDetect.notInGam.map((n, i) => (
                    <span key={n.host}>
                      {i > 0 ? ', ' : ''}
                      <strong>{n.host}</strong>
                      {` (${n.apps.slice(0, 3).join(', ')}${n.apps.length > 3 ? ` +${n.apps.length - 3}` : ''})`}
                    </span>
                  ))}
                </div>
              )}
              {firebaseDetect.loading && (
                <div className="roi-filter-hint" role="status">Looking up subdomains from Firebase…</div>
              )}
              {firebaseDetect.done && firebaseDetect.connected && (firebaseDetect.linked > 0 || firebaseDetect.missing > 0) && (
                <div className="roi-filter-hint" role="status">
                  {firebaseDetect.linked > 0 && `Firebase linked subdomains for ${firebaseDetect.linked} app${firebaseDetect.linked === 1 ? '' : 's'} automatically. `}
                  {firebaseDetect.missing > 0 && `${firebaseDetect.missing} app${firebaseDetect.missing === 1 ? ' has' : 's have'} no subdomain in the latest version — pick one above if needed.`}
                </div>
              )}
              <RoiFilterRow icon="countries" label="Countries">
                <MultiSelect
                  options={roiCountryOptions}
                  value={filterCountryCodes}
                  onChange={setFilterCountryCodes}
                  placeholder={
                    roiCountriesLoading
                      ? 'Loading countries from Ads…'
                      : (roiCountryOptions.length
                        ? 'Filter by user country (Ads spend)…'
                        : 'No country spend synced yet — run Ads sync')
                  }
                  disabled={!roiCountryOptions.length && !roiCountriesLoading}
                  loading={roiCountriesLoading}
                  searchable
                  showSelectAll
                  selectAllLabel="Select all countries"
                />
              </RoiFilterRow>
            </RoiFilterSection>

            <div className="roi-filter-notes">
              {!roiAccountOptions.length ? (
                <p className="form-note">
                  No Google Ads accounts found. Connect accounts in <strong>Admin → Google Ads accounts</strong>,
                  then run <strong>Sync</strong> so spend and campaigns are available.
                </p>
              ) : null}
              {roiAccountOptions.length > 0 && roiAccountsWithSpendInRange === 0 ? (
                <p className="form-note page-restriction-note">
                  No Ads spend synced for <strong>{startDate === endDate ? startDate : `${startDate} → ${endDate}`}</strong> yet.
                  Accounts still appear so you can filter — try <strong>Yesterday</strong> or sync Ads in Admin.
                </p>
              ) : null}
              {roiCampaignsFallback ? (
                <p className="form-note">
                  No campaigns with spend on the selected date — showing campaigns from the last 30 days for picking.
                </p>
              ) : null}
              {roiCountriesFallback ? (
                <p className="form-note">
                  No country spend on the selected date — showing countries from the last 30 days for picking.
                </p>
              ) : null}
            </div>
          </div>
        )}
      </div>

      {adsSyncHealth?.needsReconnect || (adsSyncHealth?.otherProblems || []).length > 0 ? (
        <div className="warn-card warn-card-partial" role="alert" style={{ marginTop: 12 }}>
          <div className="warn-card-main" style={{ gridTemplateColumns: '1fr' }}>
            <div className="warn-card-left" style={{ borderRight: 'none' }}>
              <div className="warn-card-icon-wrap"><span aria-hidden>!</span></div>
              <div className="warn-card-body">
                <div className="warn-card-title">
                  {adsSyncHealth.needsReconnect
                    ? 'Google Ads connection needs reconnect'
                    : 'Google Ads sync reported errors'}
                </div>
                <div className="warn-card-desc">
                  {adsSyncHealth.instruction || 'Open Google Ads accounts to review sync errors.'}
                  {(adsSyncHealth.authProblems || adsSyncHealth.otherProblems || []).slice(0, 3).map((p) => (
                    <div key={p.id} className="ads-sync-err" style={{ maxWidth: '100%', marginTop: 8 }} title={p.lastSyncError}>
                      {p.descriptiveName || p.customerId}: {p.lastSyncError || 'Needs reconnect'}
                    </div>
                  ))}
                </div>
                <div className="warn-card-btns">
                  <Link
                    className="warn-btn-primary"
                    to={adsSyncHealth.fixPath || (user?.role === 'domain_user' ? '/my-ads' : '/admin?tab=ads')}
                  >
                    {adsSyncHealth.needsReconnect ? 'Fix in Google Ads accounts' : 'Open Google Ads accounts'}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {error && <div className="login-error" style={{ marginTop: 12 }}>{error}</div>}

      {showExpense && (
        <form className="filter-card" style={{ marginTop: 16 }} onSubmit={saveExpenses}>
          <div className="filter-card-head">
            <span className="filter-card-title">Add expenses (general, site, or app)</span>
            <div className="filter-actions">
              <button type="submit" className="btn-generate" disabled={expenseBusy}>
                {expenseBusy ? 'Saving…' : '✓ Save expenses'}
              </button>
              <button type="button" className="btn-reset" onClick={() => setShowExpense(false)}>Cancel</button>
            </div>
          </div>
          <div className="filter-field" style={{ marginBottom: 12, maxWidth: 220 }}>
            <label>Date (shared)</label>
            <input
              type="date"
              value={expenseSharedDate || ''}
              onChange={(e) => setExpenseSharedDate(e.target.value)}
              required
            />
          </div>
          <div className="roi-expense-pair">
            {renderExpenseFields(expense1, setExpense1, 'Expense 1')}
            {renderExpenseFields(expense2, setExpense2, 'Expense 2')}
          </div>
          <p className="form-note" style={{ marginTop: 10 }}>
            Leave amount blank to skip one slot. Site/app expenses apply only to that inventory row; general expenses apply to overall ROI.
          </p>
        </form>
      )}

      <RoiSummaryBoards
        summary={summary}
        deltas={cardDeltas}
        compareLabel={compareLabel}
        loading={loading && !overviewReady}
        networkInfo={networkInfo}
        fetchedAt={lastUpdated}
      />

      {singleAccountMode ? (
        <RoiInventoryEarnOverview
          appEarn={inventoryEarn.appEarn}
          siteEarn={inventoryEarn.siteEarn}
          totalEarn={inventoryEarn.totalEarn}
          currency={spendCurrency}
          loading={(loading || breakdownLoading) && !countryTargetBreakdown.length}
        />
      ) : null}

      {summary.unmappedSpend > 0 && (
        <div className="warn-card warn-card-partial" role="status" style={{ marginTop: 12 }}>
          <div className="warn-card-main">
            <div className="warn-card-left">
              <div className="warn-card-icon-wrap"><span aria-hidden>i</span></div>
              <div className="warn-card-body">
                <div className="warn-card-title">Unmapped Ads spend</div>
                <div className="warn-card-desc">
                  {money(summary.unmappedSpend, spendCurrency)} in this range is not mapped to a site/app
                  {summary.mappedSpend != null
                    ? ` (${money(summary.mappedSpend, spendCurrency)} is in the table)`
                    : ''}.
                  {' '}Unmapped spend is hidden from the table and from Ads spend / ROI cards.
                  Map campaigns in Admin → Google Ads accounts → Campaign mapping to include them.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {!loading && !breakdownLoading
        && !roiAccountOptions.length
        && !(Number(summary.adsSpend) > 0)
        && !(countryTargetBreakdown?.length || countryBreakdown?.length) && (
        <div className="warn-card" role="status" style={{ marginTop: 12 }}>
          <div className="warn-card-main">
            <div className="warn-card-left">
              <div className="warn-card-icon-wrap"><span aria-hidden>i</span></div>
              <div className="warn-card-body">
                <div className="warn-card-title">No Google Ads accounts in ROI</div>
                <div className="warn-card-desc">
                  Connect MCC / individual accounts in Admin → Google Ads accounts and enable Include in ROI.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {singleAccountMode ? (
        <RoiDateTable
          dailyRows={countryTargetDailyBreakdown}
          loading={breakdownLoading}
          currency={spendCurrency}
          exportName={`roi_by_date_${applied?.startDate || startDate}_${applied?.endDate || endDate}`}
        />
      ) : null}

      <RoiCountryTreeTable
        tree={countryTree}
        loading={breakdownLoading}
        search={countrySearch}
        onSearchChange={setCountrySearch}
        onPageReset={() => setCountryPage(1)}
        page={countryPage}
        pageSize={isNarrow ? 12 : 50}
        onPageChange={setCountryPage}
        density={tableDensity}
        freezeFirst
        spendCurrency={spendCurrency}
        exportName={`roi_countries_${applied?.startDate || startDate}_${applied?.endDate || endDate}`}
        className="reporting-table"
        emptyMessage="No country spend for the selected filters"
        onReset={reset}
        emptyActions={(
          <>
            <button type="button" className="btn-generate" onClick={() => applyPreset('yesterday')}>Try yesterday</button>
            <button type="button" className="btn-reset" onClick={() => applyPreset('last7')}>Try last 7 days</button>
          </>
        )}
        headerExtra={(
          <>
            <div className="table-density-toggle" role="group" aria-label="Table density">
              <button
                type="button"
                className={`table-density-btn${tableDensity === 'compact' ? ' active' : ''}`}
                onClick={() => setTableDensity('compact')}
              >
                Compact
              </button>
              <button
                type="button"
                className={`table-density-btn${tableDensity === 'comfortable' ? ' active' : ''}`}
                onClick={() => setTableDensity('comfortable')}
              >
                Comfortable
              </button>
            </div>
            {applied?.startDate && applied?.endDate ? (
              <span className="report-range">{applied.startDate} → {applied.endDate}</span>
            ) : null}
          </>
        )}
      />

      {!!(data?.expenses?.length || data?.generalExpenses?.length) && (
        <div className="filter-card" style={{ marginTop: 16 }}>
          <div className="filter-section-head">
            <span className="filter-section-title">Other expenses</span>
            <span className="filter-section-hint">General, site, and app — included in ROI on expenses</span>
          </div>
          <div className="table-wrap" style={{ marginTop: 8 }}>
            <table className="data-table report-table report-table--comfortable">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Site / App</th>
                  <th>Label</th>
                  <th>Amount</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(data.expenses || data.generalExpenses || []).map((e) => (
                  <tr key={e.id}>
                    <td>{e.expenseDate}</td>
                    <td>
                      {e.targetType === 'site'
                        ? 'Site'
                        : e.targetType === 'app'
                          ? 'App'
                          : 'General'}
                    </td>
                    <td>{e.targetType === 'general' ? '—' : (e.targetKey || '—')}</td>
                    <td>{e.label || 'Expense'}</td>
                    <td>{money(e.amount, spendCurrency)}</td>
                    <td>
                      <button type="button" className="btn-reset" onClick={() => deleteExpense(e.id)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const ROI_TZ_REASON = {
  hourly_behind: 'the newest hours are still being fetched',
  no_hourly_data: 'hourly data is not available yet',
  before_hourly_data: 'hourly data does not reach back to these dates',
  country_filter: 'a country filter is applied, and country figures only exist per network day',
};

/** Says which days the earnings follow. Ads spend is reported by Google Ads in its own account days. */
function RoiTimezoneNote({ info }) {
  if (!info) return null;
  if (info.applied === false) {
    return (
      <p className="tz-report-note" role="status">
        Earnings are in the network timezone ({info.networkTz || 'network'}), not {info.tz}: {ROI_TZ_REASON[info.reason] || 'the selected timezone could not be applied'}.
      </p>
    );
  }
  return (
    <p className="tz-report-note" role="status">
      Earnings follow {info.tz} days. Country rows stay in network days ({info.networkTz}), and Ads spend stays in the Google Ads account's own days.
    </p>
  );
}

/** ROI with the same timezone picker as the Dashboard and Reporting; each zone remounts the view so no numbers carry over. */
export default function Roi() {
  const { tz, options, change } = useReportTimezone();
  return (
    <>
      {options.options.length > 1 && (
        <TimezoneSwitcher
          networkTz={options.networkTz}
          options={options.options}
          value={tz}
          hourlyFrom={options.hourlyFrom}
          stale={Boolean(options.stale)}
          dataUntil={options.dataUntil}
          onChange={change}
        />
      )}
      <RoiView key={tz || 'network'} />
    </>
  );
}
